"use client";

import type { ReactNode } from "react";

import { mergeTailwindClasses } from "@/utils/utils";

export type SegmentedOption = {
  key: string;
  label: ReactNode;
  disabled?: boolean;
};

type Props = {
  options: SegmentedOption[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
  ariaLabel?: string;
};

export default function SegmentedControl({
  options,
  value,
  onChange,
  className,
  ariaLabel,
}: Props) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={mergeTailwindClasses(
        "inline-flex rounded-lg bg-[#f3f4f6] p-1 dark:bg-zinc-800",
        className,
      )}
    >
      {options.map((option) => {
        const isActive = option.key === value;

        return (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={option.disabled}
            onClick={() => {
              if (!option.disabled) {
                onChange(option.key);
              }
            }}
            className={mergeTailwindClasses(
              "rounded-md px-4 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-primary text-white shadow-sm"
                : "text-[#4b5563] hover:text-black dark:text-zinc-300 dark:hover:text-white",
              option.disabled &&
                "cursor-not-allowed opacity-50 hover:text-[#4b5563] dark:hover:text-zinc-300",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
